import { useRef, type ReactNode } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import styles from "./PickerItem.module.scss";

/**
 * The single-line row label. With `hasTruncationTooltip`, a string label
 * that is cut by its ellipsis shows in full as a hover tooltip (the overflow
 * is measured on hover, as `BreakableText` does); a label
 * that fits gets none. The text node stays as is, so the row's accessible
 * name does not change.
 */
export const PickerItemLabel = ({
    children,
    hasTruncationTooltip,
    className = styles.itemLabel,
    part,
}: {
    children: ReactNode;
    hasTruncationTooltip: boolean;
    className?: string;
    part?: string;
}) => {
    const labelRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: labelRef,
        isTruncated: isOverflowingX,
    });

    if (!hasTruncationTooltip || typeof children !== "string") {
        return (
            <span className={className} data-picker-part={part}>
                {children}
            </span>
        );
    }
    return (
        <Tooltip content={children} {...truncationTooltipProps}>
            <span ref={labelRef} className={className} data-picker-part={part}>
                {children}
            </span>
        </Tooltip>
    );
};
